import { formatPeriod, parsePeriod, toPeriod } from "@atlant/agent-contract"

// GQL CAN'T GENERATE IT...
export type DateInput = {
  plaindate: string
  plaintime: string
  timezone: string
}

// GQL CAN'T GENERATE IT...
export type DateOutput = {
  milliseconds: string
  plaindate: string
  plaintime: string
  timezone: string
  RFC3339: string
  RFC9557: string
}

export class DateConverter {
  static toServer(date?: null, timezone?: string | null): null
  static toServer(date: Date, timezone?: string | null): DateInput
  static toServer(date?: Date | null, timezone?: string | null): DateInput | null {
    if (!date) {
      return null
    }

    const zone = timezone || "UTC"
    const parts = DateConverter.getParts(date)

    return {
      plaindate: `${parts.year}-${parts.month}-${parts.day}`,
      plaintime: `${parts.hour}:${parts.minute}:${parts.second}`,
      timezone: zone,
    }
  }

  // server centralized events or user actions: createdAt, updatedAt, etc... e.g departure time in user local tz
  static fromServerEvent(output?: null): null
  static fromServerEvent(output?: DateOutput): Date
  static fromServerEvent(output?: DateOutput | null): Date | null {
    if (!output) {
      return null
    }

    return new Date(output.milliseconds)
  }

  // departure | arrival dates
  static fromServer(output?: null): null
  static fromServer(output: DateOutput): Date
  static fromServer(output?: DateOutput | null): Date | null {
    if (!output) {
      return null
    }

    // requires test on safari
    return new Date(output.RFC3339)
  }

  static getParts(date: Date) {
    const pad = (n: number) => String(n).padStart(2, "0")

    return {
      year: String(date.getFullYear()),
      month: pad(date.getMonth() + 1),
      day: pad(date.getDate()),
      hour: pad(date.getHours()),
      minute: pad(date.getMinutes()),
      second: pad(date.getSeconds()),
    }
  }

  private static months = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ]

  static formatDisplayDate(plaindate: string, format?: string): string {
    const [year, month, day] = plaindate.split("-")
    const m = Number(month)
    const monthName = DateConverter.months[m - 1] ?? month

    switch (format) {
      case "mm_dd_yyyy":
        return `${month}/${day}/${year}`
      case "dd_mm_yyyy":
        return `${day}/${month}/${year}`
      case "yyyy_mm_dd":
        return `${year}-${month}-${day}`
      case "dd_mmm_yyyy":
      default:
        return `${Number(day)} ${monthName} ${year}`
    }
  }

  // Invoice periods (URL param): YYYY-MM. Shared with the assistant, which links to them.
  static toPeriod = toPeriod

  static parsePeriod = parsePeriod

  // Sep 2026
  static formatPeriod = formatPeriod

  static formatDisplayTime(
    plaintime: string,
    timezone: string,
    timeFormat?: string,
  ): string {
    const [h, m] = plaintime.split(":")
    const hour = Number(h)
    const minute = m

    const tzShort = DateConverter.#shortTz(timezone)

    if (timeFormat === "TWELVE_HOUR") {
      const period = hour >= 12 ? "PM" : "AM"
      const h12 = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour
      return `${h12}:${minute} ${period} ${tzShort}`
    }

    return `${String(hour).padStart(2, "0")}:${minute} ${tzShort}`
  }

  // Duration as HH:MM; hours are not wrapped at 24 (e.g. 1815 min -> "30:15")
  static formatDuration(minutes: number): string {
    const total = Math.max(0, Math.round(minutes))
    const h = Math.floor(total / 60)
    const m = total % 60
    return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`
  }

  static #shortTz(timezone: string): string {
    try {
      const parts = new Intl.DateTimeFormat("en-US", {
        timeZone: timezone,
        timeZoneName: "short",
      }).formatToParts(new Date())
      return parts.find((p) => p.type === "timeZoneName")?.value ?? timezone
    } catch {
      return timezone.split("/").pop()?.replace(/_/g, " ") ?? timezone
    }
  }
}
